import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { X, MapPin, Clock, User } from 'lucide-react'
import { config, apiEndpoints } from '../config/env'
import { workOrderService, type TechnicianDayDestination } from '../services/workOrderService'
import { geotabService, type GeotabDevice } from '../services/geotabService'
import { useQuery } from '@tanstack/react-query'

interface WorkOrderLocation {
  workOrderID: number
  jobNo: string
  pinName: string
  fullAddress: string
  latitude?: number
  longitude?: number
  clientID?: number
  clientName: string
  clientIcon?: string
  status: string
  inspectionType: string
  scheduledDate?: string
  technicianName: string
  techOrderNo?: number | null
  keyPickup?: boolean
  keyDropoff?: boolean
  keyPickupAddress?: string
  keyDropoffAddress?: string
}

interface WorkOrderMapProps {
  workOrders?: WorkOrderLocation[]
  dayDestinations?: TechnicianDayDestination[]
  technicianId?: number
  date: string
  status?: string
  onClose?: () => void
  inline?: boolean
}

// Google Maps type definitions
interface GoogleMapsLatLng {
  lat: number
  lng: number
}

interface GoogleMapsLatLngBounds {
  extend: (point: GoogleMapsLatLng) => void
}

interface GoogleMapsGeocodeResult {
  geometry: {
    location: GoogleMapsLatLng
  }
}

interface GoogleMap {
  setCenter: (center: GoogleMapsLatLng) => void
  setZoom: (zoom: number) => void
  fitBounds: (bounds: GoogleMapsLatLngBounds) => void
  getZoom: () => number
}

interface GoogleMapsAdvancedMarker {
  map: GoogleMap | null
  position: GoogleMapsLatLng | { lat: number; lng: number }
  title: string
  content?: HTMLElement
  addListener: (event: string, handler: () => void) => void
}

interface GoogleMapsGeocoder {
  geocode: (
    request: { address: string },
    callback: (results: GoogleMapsGeocodeResult[], status: string) => void
  ) => void
}

interface GoogleMapsInfoWindow {
  open: (options: { map: GoogleMap | null; anchor: GoogleMapsAdvancedMarker }) => void
}

interface GoogleMapsMapOptions {
  center: GoogleMapsLatLng
  zoom: number
  mapTypeControl: boolean
  streetViewControl: boolean
  fullscreenControl: boolean
  mapId?: string
}

interface GoogleMapsMarker {
  setMap: (map: GoogleMap | null) => void
  addListener: (event: string, handler: () => void) => void
}

interface GoogleMapsSize {
  width: number
  height: number
}

interface GoogleMapsMarkerOptions {
  position: GoogleMapsLatLng | { lat: number; lng: number }
  map: GoogleMap | null
  title: string
  label?: string | { text: string; color: string; fontSize?: string; fontWeight?: string }
  icon?: string | {
    url: string
    scaledSize?: GoogleMapsSize
  }
  zIndex?: number
}

interface GoogleMapsPolylineOptions {
  path: Array<GoogleMapsLatLng | { lat: number; lng: number }>
  map: GoogleMap | null
  strokeColor?: string
  strokeOpacity?: number
  strokeWeight?: number
  geodesic?: boolean
}

interface GoogleMapsPolyline {
  setMap: (map: GoogleMap | null) => void
}

interface GoogleMapsDirectionsResult {
  routes: unknown[]
}

interface GoogleMapsDirectionsService {
  route: (
    request: {
      origin: GoogleMapsLatLng | { lat: number; lng: number }
      destination: GoogleMapsLatLng | { lat: number; lng: number }
      waypoints?: Array<{ location: GoogleMapsLatLng | { lat: number; lng: number }; stopover: boolean }>
      travelMode: string
      optimizeWaypoints?: boolean
    },
    callback: (result: GoogleMapsDirectionsResult | null, status: string) => void
  ) => void
}

interface GoogleMapsDirectionsRendererOptions {
  map?: GoogleMap | null
  suppressMarkers?: boolean
  polylineOptions?: {
    strokeColor?: string
    strokeOpacity?: number
    strokeWeight?: number
  }
}

interface GoogleMapsDirectionsRenderer {
  setMap: (map: GoogleMap | null) => void
  setDirections: (directions: GoogleMapsDirectionsResult) => void
  setOptions: (options: GoogleMapsDirectionsRendererOptions) => void
}

interface GoogleMapsOverlayView {
  setMap: (map: GoogleMap | null) => void
  draw: () => void
  onAdd: () => void
  onRemove: () => void
  getPanes: () => { floatPane: HTMLElement; overlayLayer: HTMLElement }
  getProjection: () => { fromLatLngToDivPixel: (latLng: GoogleMapsLatLng | { lat: number; lng: number }) => { x: number; y: number } }
}

declare global {
  interface Window {
    google: {
      maps: {
        Map: new (element: HTMLElement, options: GoogleMapsMapOptions) => GoogleMap
        Marker: new (options: GoogleMapsMarkerOptions) => GoogleMapsMarker
        InfoWindow: new (options: { content: string }) => GoogleMapsInfoWindow
        Geocoder: new () => GoogleMapsGeocoder
        LatLngBounds: new () => GoogleMapsLatLngBounds
        Size: new (width: number, height: number) => GoogleMapsSize
        Polyline: new (options: GoogleMapsPolylineOptions) => GoogleMapsPolyline
        DirectionsService: new () => GoogleMapsDirectionsService
        DirectionsRenderer: new (options?: GoogleMapsDirectionsRendererOptions) => GoogleMapsDirectionsRenderer
        OverlayView: new () => GoogleMapsOverlayView
        Point: new (x: number, y: number) => { x: number; y: number }
        TravelMode: {
          DRIVING: string
        }
      }
    }
    gm_authFailure?: () => void
  }
}

const WorkOrderMap: React.FC<WorkOrderMapProps> = ({ workOrders, dayDestinations, technicianId, date, status, onClose, inline = false }) => {
  const [locations, setLocations] = useState<WorkOrderLocation[]>(workOrders || [])
  const [map, setMap] = useState<GoogleMap | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [geocoder, setGeocoder] = useState<GoogleMapsGeocoder | null>(null)
  const [markers, setMarkers] = useState<GoogleMapsMarker[]>([])
  const [selectedLocation, setSelectedLocation] = useState<WorkOrderLocation | null>(null)
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [technicianMarker, setTechnicianMarker] = useState<GoogleMapsMarker | null>(null)
  const [directionsRenderer, setDirectionsRenderer] = useState<GoogleMapsDirectionsRenderer | null>(null)
  const [orderNumberOverlays, setOrderNumberOverlays] = useState<HTMLElement[]>([])

  // Fetch technician location from Geotab
  const { data: geotabLocation } = useQuery<GeotabDevice | null>({
    queryKey: ['geotab-location', technicianId],
    queryFn: () => technicianId ? geotabService.getTechnicianLocation(technicianId) : Promise.resolve(null),
    enabled: !!technicianId,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Check if Google Maps is fully loaded (including Map constructor)
  const isGoogleMapsScriptLoaded = useCallback(() => {
    return typeof window !== 'undefined' &&
           window.google &&
           window.google.maps &&
           typeof window.google.maps.Map === 'function'
  }, [])

  // Initialize Google Maps
  useEffect(() => {
    let retryCount = 0
    const maxRetries = 50 // 5 seconds max (50 * 100ms)

    const initializeMap = () => {
      // Wait for the DOM element to be available
      const mapElement = mapContainerRef.current
      if (!mapElement) {
        // Retry after a short delay if container not ready
        if (retryCount < maxRetries) {
          retryCount++
          setTimeout(() => {
            if (mapContainerRef.current) {
              initializeMap()
            }
          }, 100)
        }
        return
      }

      if (!window.google || !window.google.maps || typeof window.google.maps.Map !== 'function') {
        // API not fully loaded yet, retry after a short delay
        if (retryCount < maxRetries) {
          retryCount++
          setTimeout(() => {
            initializeMap()
          }, 100)
        } else {
          setError('Google Maps API failed to load completely')
          setIsLoading(false)
        }
        return
      }

      // Check if API key is configured
      if (!config.googleMapsApiKey) {
        setError('Google Maps API key not configured')
        setIsLoading(false)
        return
      }

      // Add error listener for API errors
      window.gm_authFailure = () => {
        setError('Google Maps API not enabled. Please enable the Maps JavaScript API in Google Cloud Console.')
        setIsLoading(false)
      }

      try {
        // Create map centered on Sunshine Coast
        const mapInstance = new window.google.maps.Map(mapElement, {
          center: { lat: -26.65, lng: 153.07 },
          zoom: 11,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true
        }) as GoogleMap

        const geocoderInstance = new window.google.maps.Geocoder() as GoogleMapsGeocoder

        setMap(mapInstance)
        setGeocoder(geocoderInstance)
        setIsGoogleMapsLoaded(true)
        setError(null)
      } catch (err) {
        console.error('Error initializing Google Maps:', err)
        setError('Failed to initialize Google Maps')
        setIsLoading(false)
      }
    }

    // Check if Google Maps is already loaded
    if (isGoogleMapsScriptLoaded()) {
      // Add a small delay to ensure DOM is ready
      setTimeout(initializeMap, 100)
      return
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
    if (existingScript) {
      // Script is already loading, wait for it
             const checkLoaded = setInterval(() => {
         if (isGoogleMapsScriptLoaded()) {
           clearInterval(checkLoaded)
           setTimeout(initializeMap, 100)
         }
       }, 100)
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkLoaded)
        if (!isGoogleMapsScriptLoaded()) {
          setError('Google Maps API failed to load')
          setIsLoading(false)
        }
      }, 10000)
      return
    }

    // Load Google Maps script with async loading pattern
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=geometry,places&loading=async`
    script.async = true
    script.defer = true
    script.onload = () => {
      setIsGoogleMapsLoaded(true)
      setTimeout(initializeMap, 100)
    }
    script.onerror = () => {
      setError('Failed to load Google Maps API')
      setIsLoading(false)
    }
    document.head.appendChild(script)

    // Cleanup function
    return () => {
      // Don't remove the script as it might be used by other components
      // Just clear the loading state
      setIsGoogleMapsLoaded(false)
    }
  }, [isGoogleMapsScriptLoaded])

  // Fetch work order locations (only if not provided via props)
  const fetchLocations = useCallback(async () => {
    // If workOrders are provided via props, use them directly
    if (workOrders && workOrders.length > 0) {
      setLocations(workOrders)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const params = new URLSearchParams({
        date: date
      })
      
      if (technicianId) {
        params.append('technicianId', technicianId.toString())
      }
      
      if (status && status !== 'all') {
        params.append('status', status)
      }

      const response = await fetch(`${apiEndpoints.workOrders.mapLocations}?${params}`)
      
      if (!response.ok) {
        throw new Error('Failed to fetch work order locations')
      }

      const data: WorkOrderLocation[] = await response.json()
      setLocations(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }, [workOrders, technicianId, date, status])



  const geocodeAddress = (address: string): Promise<GoogleMapsGeocodeResult[]> => {
    return new Promise((resolve, reject) => {
      if (!geocoder) {
        reject(new Error('Geocoder not available'))
        return
      }
      
      geocoder.geocode({ address }, (results: GoogleMapsGeocodeResult[], status: string) => {
        if (status === 'OK') {
          resolve(results)
        } else {
          reject(new Error(`Geocoding failed: ${status}`))
        }
      })
    })
  }

  const createMarker = (location: WorkOrderLocation | { address: string, type: 'pickup' | 'dropoff' }, position: GoogleMapsLatLng | { lat: number; lng: number }) => {
    let title = ''

    if ('type' in location) {
      // Key pickup/dropoff marker
      title = location.type === 'pickup'
        ? `Key Pickup: ${location.address}`
        : `Key Dropoff: ${location.address}`
    } else {
      // Regular work order marker
      title = location.clientName
    }

    // Create standard Google Maps marker with scaled icon
    const marker = new window.google.maps.Marker({
      position,
      map,
      title,
      icon: {
        url: 'http://maps.google.com/mapfiles/kml/paddle/orange-circle.png',
        scaledSize: new window.google.maps.Size(40, 40)
      }
    }) as GoogleMapsMarker

    // Create info window
    let infoContent = ''
    
    if ('type' in location) {
      // Key pickup/dropoff info window
      infoContent = `
        <div style="max-width: 300px;">
          <h4 style="margin: 0 0 8px 0; color: #1f2937;">
            ${location.type === 'pickup' ? '🔑 Key Pickup' : '🔑 Key Dropoff'}
          </h4>
          <p style="margin: 4px 0;">
            <strong>Address:</strong> ${location.address}
          </p>
        </div>
      `
    } else {
      // Regular work order info window
      infoContent = `
        <div style="max-width: 300px;">
          <h4 style="margin: 0 0 8px 0; color: #1f2937;">${location.pinName}</h4>
          <table style="width: 100%; font-size: 14px;">
            <tr>
              <td style="padding: 2px 8px 2px 0; font-weight: bold; width: 80px;">Client:</td>
              <td style="padding: 2px 0;">${location.clientName}</td>
            </tr>
            <tr>
              <td style="padding: 2px 8px 2px 0; font-weight: bold;">Address:</td>
              <td style="padding: 2px 0;">${location.fullAddress}</td>
            </tr>
            <tr>
              <td style="padding: 2px 8px 2px 0; font-weight: bold;">Type:</td>
              <td style="padding: 2px 0;">${location.inspectionType}</td>
            </tr>
            <tr>
              <td style="padding: 2px 8px 2px 0; font-weight: bold;">Status:</td>
              <td style="padding: 2px 0;">${location.status}</td>
            </tr>
            <tr>
              <td style="padding: 2px 8px 2px 0; font-weight: bold;">Technician:</td>
              <td style="padding: 2px 0;">${location.technicianName}</td>
            </tr>
            ${location.keyPickup ? '<tr><td colspan="2" style="padding: 4px 0; color: #34A853; font-weight: bold;">🔑 Key Pickup Required</td></tr>' : ''}
            ${location.keyDropoff ? '<tr><td colspan="2" style="padding: 4px 0; color: #FBBC04; font-weight: bold;">🔑 Key Dropoff Required</td></tr>' : ''}
          </table>
        </div>
      `
    }
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent
    })

    // Add click listener to marker
    marker.addListener('click', () => {
      infoWindow.open({ map, anchor: marker })
      if (!('type' in location)) {
        setSelectedLocation(location)
      }
    })

    return marker
  }

  const createOrderNumberOverlay = (position: GoogleMapsLatLng | { lat: number; lng: number }, orderNumber: number) => {
    if (!map || orderNumber >= 9999) return null

    // Create a custom overlay for the order number
    const overlay = document.createElement('div')
    overlay.style.position = 'absolute'
    overlay.style.backgroundColor = 'white'
    overlay.style.color = 'black'
    overlay.style.padding = '3px 7px'
    overlay.style.borderRadius = '10px'
    overlay.style.fontSize = '11px'
    overlay.style.fontWeight = 'bold'
    overlay.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)'
    overlay.style.zIndex = '1000'
    overlay.style.pointerEvents = 'none'
    overlay.textContent = orderNumber.toString()

    // We'll need to update its position when the map moves/zooms
    // For now, we'll use a simple approach with a custom OverlayView
    type CustomOverlayType = GoogleMapsOverlayView & { position: GoogleMapsLatLng | { lat: number; lng: number }; content: HTMLElement }
    const CustomOverlay = function(this: CustomOverlayType, position: GoogleMapsLatLng | { lat: number; lng: number }, content: HTMLElement, map: GoogleMap) {
      this.position = position
      this.content = content
      this.setMap(map)
    }

    // @ts-ignore
    CustomOverlay.prototype = new window.google.maps.OverlayView()

    // @ts-ignore
    CustomOverlay.prototype.onAdd = function() {
      const panes = this.getPanes()
      panes.floatPane.appendChild(this.content)
    }

    // @ts-ignore
    CustomOverlay.prototype.draw = function() {
      const projection = this.getProjection()
      const point = projection.fromLatLngToDivPixel(this.position)

      if (point) {
        // Position at top-right of marker (marker is 40x40, offset by 8px right and -32px up to be closer)
        this.content.style.left = (point.x + 2) + 'px'
        this.content.style.top = (point.y - 45) + 'px'
      }
    }

    // @ts-ignore
    CustomOverlay.prototype.onRemove = function() {
      if (this.content.parentElement) {
        this.content.parentElement.removeChild(this.content)
      }
    }

    // @ts-ignore
    new CustomOverlay(position, overlay, map)

    return overlay
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'default'
      case 'in progress': return 'secondary'
      case 'scheduled': return 'outline'
      case 'cancelled': return 'destructive'
      default: return 'outline'
    }
  }

  // Load locations when component mounts or filters change
  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  // Create a stable data key to track when data actually changes
  const dataKey = useMemo(() => {
    const locationsKey = locations.map(l => `${l.workOrderID}-${l.status}`).join(',')
    const destinationsKey = dayDestinations?.filter(d => !d.completed).map(d => `${d.technicianDayDestinationID}-${d.completed}`).join(',') || ''
    return `${locationsKey}|${destinationsKey}`
  }, [locations, dayDestinations])

  // Create markers when everything is loaded
  useEffect(() => {
    const createMarkers = async () => {
      // Check all required dependencies
      if (!map || !geocoder || !isGoogleMapsLoaded) {
        return
      }

      // Need at least some data to display
      if ((!locations || locations.length === 0) && (!dayDestinations || dayDestinations.length === 0)) {
        return
      }

      // Clear existing markers, route renderer, and overlays
      markers.forEach(marker => marker.setMap(null))
      setMarkers([])

      if (directionsRenderer) {
        directionsRenderer.setMap(null)
        setDirectionsRenderer(null)
      }

      // Clear existing order number overlays
      orderNumberOverlays.forEach(overlay => {
        if (overlay.parentElement) {
          overlay.parentElement.removeChild(overlay)
        }
      })
      setOrderNumberOverlays([])

      // Build combined list of all stops in order
      const allDestinations: Array<{
        type: 'workorder' | 'pickup' | 'dropoff'
        order: number
        address: string
        data: WorkOrderLocation | TechnicianDayDestination
      }> = []

      // Add work orders - show all work orders even without techOrderNo
      locations.forEach((wo, index) => {
        allDestinations.push({
          type: 'workorder',
          order: wo.techOrderNo ?? (9999 + index), // Use high number for unordered items
          address: wo.fullAddress,
          data: wo
        })
      })

      // Build set of addresses where PU is still incomplete → hide their DO
      const incompletePUAddresses = new Set(
        dayDestinations
          ?.filter(d => d.destinationType === 'KeyPickup' && !d.completed)
          .map(d => d.address) ?? []
      )

      // Add key pickups and dropoffs (exclude completed ones)
      dayDestinations?.forEach((dest, index) => {
        // Skip completed destinations
        if (dest.completed) {
          return
        }

        if (dest.destinationType === 'KeyPickup') {
          allDestinations.push({
            type: 'pickup',
            order: dest.orderSequence ?? (10000 + index),
            address: dest.address,
            data: dest
          })
        } else if (dest.destinationType === 'KeyDropoff') {
          // Hide DO until matching PU is completed
          if (incompletePUAddresses.has(dest.address)) {
            return
          }
          allDestinations.push({
            type: 'dropoff',
            order: dest.orderSequence ?? (11000 + index),
            address: dest.address,
            data: dest
          })
        }
      })

      // Sort by order
      allDestinations.sort((a, b) => a.order - b.order)

      if (allDestinations.length === 0) {
        return
      }

      // Geocode all addresses and create markers
      const newMarkers: GoogleMapsMarker[] = []
      const newOverlays: HTMLElement[] = []
      const bounds = new window.google.maps.LatLngBounds()
      const addressCache = new Map<string, GoogleMapsLatLng | { lat: number; lng: number }>()
      const routeWaypoints: Array<{ position: GoogleMapsLatLng | { lat: number; lng: number }; order: number }> = []

      for (const dest of allDestinations) {
        try {
          let position: GoogleMapsLatLng | { lat: number; lng: number }

          // Check if work order has coordinates from API
          if (dest.type === 'workorder') {
            const wo = dest.data as WorkOrderLocation
            if (wo.latitude && wo.longitude) {
              // Use lat/lng from backend - no geocoding needed
              position = { lat: wo.latitude, lng: wo.longitude }
            } else if (addressCache.has(dest.address)) {
              position = addressCache.get(dest.address)!
            } else {
              // Fallback: geocode only if backend didn't provide coordinates
              const results = await geocodeAddress(dest.address)
              if (results && results.length > 0) {
                position = results[0].geometry.location
                addressCache.set(dest.address, position)
              } else {
                continue
              }
            }
          } else {
            // Key pickup/dropoff - use backend lat/lng if available, then address cache, then geocode
            const destData = dest.data as TechnicianDayDestination
            if (destData.latitude && destData.longitude) {
              // Use lat/lng from backend - no geocoding needed
              position = {
                lat: destData.latitude + (dest.type === 'pickup' ? 0.0001 : -0.0001),
                lng: destData.longitude + (dest.type === 'pickup' ? -0.0001 : 0.0001)
              }
            } else if (addressCache.has(dest.address)) {
              const basePos = addressCache.get(dest.address)!
              // Apply small offset for visibility
              position = {
                lat: basePos.lat + (dest.type === 'pickup' ? 0.0001 : -0.0001),
                lng: basePos.lng + (dest.type === 'pickup' ? -0.0001 : 0.0001)
              }
            } else {
              const results = await geocodeAddress(dest.address)
              if (results && results.length > 0) {
                position = results[0].geometry.location
                addressCache.set(dest.address, position)
                // Write back to backend so future loads skip geocoding
                const lat = 'lat' in position ? position.lat : (position as google.maps.LatLng).lat()
                const lng = 'lng' in position ? position.lng : (position as google.maps.LatLng).lng()
                workOrderService.updateDestinationLocation(destData.technicianDayDestinationID, lat, lng).catch(() => {})
              } else {
                continue
              }
            }
          }

          // Create marker
          const marker = dest.type === 'workorder'
            ? createMarker(dest.data as WorkOrderLocation, position)
            : createMarker({ address: dest.address, type: dest.type }, position)

          newMarkers.push(marker)
          bounds.extend(position)

          // Create order number overlay
          const overlay = createOrderNumberOverlay(position, dest.order)
          if (overlay) {
            newOverlays.push(overlay)
          }

          // Add to route waypoints
          routeWaypoints.push({ position, order: dest.order })
        } catch (error) {
          console.error(`Error processing destination ${dest.order}:`, error)
        }
      }

      setMarkers(newMarkers)
      setOrderNumberOverlays(newOverlays)

      // Draw route using Google Directions API for actual navigation routes
      if (routeWaypoints.length > 1) {
        const directionsService = new window.google.maps.DirectionsService()
        const renderer = new window.google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true, // We already have our custom markers
          polylineOptions: {
            strokeColor: '#2563EB',
            strokeOpacity: 0.8,
            strokeWeight: 4
          }
        })

        // For Directions API, we need origin, destination, and waypoints (max 25)
        const origin = routeWaypoints[0].position
        const destination = routeWaypoints[routeWaypoints.length - 1].position
        const waypoints = routeWaypoints.slice(1, -1).slice(0, 25).map(wp => ({
          location: wp.position,
          stopover: true
        }))

        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false // Keep our order
          },
          (result, status) => {
            if (status === 'OK' && result) {
              renderer.setDirections(result)
              setDirectionsRenderer(renderer)
            } else {
              console.error('Directions request failed:', status)
            }
          }
        )
      }

      // Fit map to bounds
      if (newMarkers.length > 0) {
        map.fitBounds(bounds)
        if (newMarkers.length === 1) {
          map.setZoom(Math.min(map.getZoom(), 15))
        }
      }
    }

    createMarkers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, geocoder, isGoogleMapsLoaded, dataKey])

  // Update technician location marker from Geotab
  useEffect(() => {
    console.log('🚗 Geotab location update:', {
      hasMap: !!map,
      isLoaded: isGoogleMapsLoaded,
      geotabData: geotabLocation
    })

    if (!map || !isGoogleMapsLoaded || !geotabLocation) {
      console.log('🚗 Missing dependencies for car marker')
      return
    }

    // Remove existing technician marker
    if (technicianMarker) {
      console.log('🚗 Removing old car marker')
      technicianMarker.setMap(null)
    }

    // Check if we have valid coordinates
    const lat = geotabLocation.deviceCurrentLatitude
    // Handle both correct spelling and backend typo
    const lng = geotabLocation.deviceCurrentLongitude ?? geotabLocation.deviceCurrentLogitude

    console.log('🚗 Car coordinates:', { lat, lng })

    if (!lat || !lng) {
      console.log('🚗 No valid coordinates for car marker')
      return
    }

    console.log('🚗 Creating car marker at:', { lat, lng })

    // Create custom car marker content with driver name prominently displayed
    const carMarkerContent = document.createElement('div')
    carMarkerContent.style.textAlign = 'center'
    carMarkerContent.innerHTML = `
      <div style="text-align: center;">
        <img src="https://www.adminsunstatesmokealarms.com.au/assets/images/car_orange.png"
             style="width: 2.5em; height: 2.5em; display: block; margin: 0 auto;" />
        <div style="color: #000; font-size: 1.2em; font-weight: bold; margin-top: 2px;
                    text-shadow: 1px 1px 2px white, -1px -1px 2px white, 1px -1px 2px white, -1px 1px 2px white;">
          ${geotabLocation.driverName || 'Technician'}
        </div>
      </div>
    `

    // Create technician location marker with custom content
    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      title: `${geotabLocation.driverName || 'Technician'} - Current Location`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="1" height="1">
            <rect width="1" height="1" fill="transparent"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(1, 1),
        anchor: new window.google.maps.Point(0, 0)
      },
      zIndex: 1000 // Ensure car appears above other markers
    }) as GoogleMapsMarker

    console.log('🚗 Car marker created successfully')

    // Create custom overlay for car marker with label
    type CarMarkerOverlayType = GoogleMapsOverlayView & { position: GoogleMapsLatLng | { lat: number; lng: number }; content: HTMLElement }
    const CarMarkerOverlay = function(this: CarMarkerOverlayType, position: GoogleMapsLatLng | { lat: number; lng: number }, content: HTMLElement, map: GoogleMap) {
      this.position = position
      this.content = content
      this.setMap(map)
    }

    // @ts-expect-error - Google Maps OverlayView prototype
    CarMarkerOverlay.prototype = new window.google.maps.OverlayView()

    // @ts-expect-error - Google Maps OverlayView prototype
    CarMarkerOverlay.prototype.onAdd = function() {
      const panes = this.getPanes()
      panes.overlayLayer.appendChild(this.content)
    }

    // @ts-expect-error - Google Maps OverlayView prototype
    CarMarkerOverlay.prototype.draw = function() {
      const projection = this.getProjection()
      const point = projection.fromLatLngToDivPixel(this.position)

      if (point) {
        this.content.style.position = 'absolute'
        this.content.style.left = (point.x - 20) + 'px' // Center horizontally
        this.content.style.top = (point.y - 35) + 'px' // Position above point
      }
    }

    // @ts-expect-error - Google Maps OverlayView prototype
    CarMarkerOverlay.prototype.onRemove = function() {
      if (this.content.parentElement) {
        this.content.parentElement.removeChild(this.content)
      }
    }

    // @ts-expect-error - Google Maps OverlayView constructor
    new CarMarkerOverlay({ lat, lng }, carMarkerContent, map)

    // Add info window
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="max-width: 200px;">
          <h4 style="margin: 0 0 8px 0; color: #1f2937;">
            📍 Current Location
          </h4>
          <p style="margin: 4px 0; font-size: 14px;">
            <strong>${geotabLocation.driverName || 'Technician'}</strong>
          </p>
          <p style="margin: 4px 0; font-size: 12px; color: #666;">
            ${geotabLocation.deviceName || 'Device'}<br/>
            Updated: ${new Date(geotabLocation.dateCreated).toLocaleTimeString()}
          </p>
        </div>
      `
    })

    marker.addListener('click', () => {
      infoWindow.open({ map, anchor: marker })
    })

    setTechnicianMarker(marker)
  }, [map, isGoogleMapsLoaded, geotabLocation])

  // Render inline version (no modal wrapper)
  if (inline) {
    return (
      <Card className="w-full h-[500px] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              Work Order Locations - {new Date(date).toLocaleDateString('en-AU')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {locations.length} work order{locations.length !== 1 ? 's' : ''} found
              {technicianId && ' for selected technician'}
              {status && status !== 'all' && ` with status: ${status}`}
            </p>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0">
          <div className="flex-1 flex">
            {/* Map container - always rendered but may be hidden */}
            <div className="flex-1 relative">
              <div ref={mapContainerRef} className="w-full h-full"></div>

              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading work order locations...</p>
                  </div>
                </div>
              )}

              {/* Error overlay */}
              {error && !isLoading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center text-red-600">
                    <p className="font-semibold">Error loading map</p>
                    <p className="text-sm">{error}</p>
                    <Button onClick={fetchLocations} className="mt-4">
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Render modal version
  return (
    <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              Work Order Locations - {new Date(date).toLocaleDateString('en-AU')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {locations.length} work order{locations.length !== 1 ? 's' : ''} found
              {technicianId && ' for selected technician'}
              {status && status !== 'all' && ` with status: ${status}`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0">
          <div className="flex-1 flex">
            {/* Map container - always rendered but may be hidden */}
            <div className="flex-1 relative">
              <div ref={mapContainerRef} className="w-full h-full"></div>
              
              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading work order locations...</p>
                  </div>
                </div>
              )}
              
              {/* Error overlay */}
              {error && !isLoading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center text-red-600">
                    <p className="font-semibold">Error loading map</p>
                    <p className="text-sm">{error}</p>
                    <Button onClick={fetchLocations} className="mt-4">
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar with work order details */}
            {!isLoading && !error && selectedLocation && (
                <div className="w-80 border-l bg-gray-50 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg">{selectedLocation.pinName}</h3>
                      <Badge variant={getStatusBadgeVariant(selectedLocation.status)} className="mt-1">
                        {selectedLocation.status}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{selectedLocation.clientName}</p>
                          <p className="text-xs text-gray-500">Client</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm">{selectedLocation.fullAddress}</p>
                          <p className="text-xs text-gray-500">Property Address</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm">{selectedLocation.inspectionType}</p>
                          <p className="text-xs text-gray-500">Inspection Type</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm">{selectedLocation.technicianName}</p>
                          <p className="text-xs text-gray-500">Technician</p>
                        </div>
                      </div>

                      {selectedLocation.scheduledDate && (
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm">
                              {new Date(selectedLocation.scheduledDate).toLocaleDateString('en-AU')}
                            </p>
                            <p className="text-xs text-gray-500">Scheduled Date</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t">
                      <Button 
                        className="w-full" 
                        onClick={() => window.open(`/work-orders/${selectedLocation.workOrderID}`, '_blank')}
                      >
                        View Work Order
                      </Button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default WorkOrderMap